/**
 * NotificationBell — topbar bell icon with unread badge + popover dropdown.
 *
 * Polling: delegates to useUnreadCountQuery (15 s interval) for the badge,
 * and useNotificationsQuery (30 s interval) for the dropdown list.
 *
 * Bell badge:
 * - Hidden when unread_count === 0.
 * - Shows "9+" when unread_count > 9.
 * - aria-label="{N} unread notifications" for screen readers.
 *
 * Popover:
 * - Heading "Notifications" + unread-only filter toggle.
 * - Top 10 NotificationCards.
 * - "Mark all as read" button (calls markRead with all visible unread IDs).
 * - "See all" link → /notifications.
 * - Empty state: "You're all caught up — nothing new to read."
 *
 * Accessibility: focus trap is handled by Radix Popover.
 * Focus returns to the bell trigger on close (Radix default).
 */

import { useState } from 'react';
import { Bell } from 'lucide-react';
import { Link } from 'react-router-dom';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Skeleton } from '@/components/ui/skeleton';

import { useUnreadCountQuery, useNotificationsQuery, useMarkReadMutation } from '@/hooks/api/useNotifications';
import { NotificationCard } from './NotificationCard';

const BELL_LIMIT = 10;

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [unreadOnly, setUnreadOnly] = useState(false);

  const { data: countData } = useUnreadCountQuery();
  const unreadCount = countData?.unread_count ?? 0;

  const { data: listData, isLoading } = useNotificationsQuery({
    unread_only: unreadOnly,
    page: 1,
    page_size: BELL_LIMIT,
  });

  const markReadMutation = useMarkReadMutation();

  const notifications = listData?.items ?? [];
  const unreadIds = notifications
    .filter((n) => n.read_at == null)
    .map((n) => n.id);

  const badgeText = unreadCount > 9 ? '9+' : String(unreadCount);
  const badgeAriaLabel =
    unreadCount === 0
      ? 'No unread notifications'
      : `${unreadCount} unread notification${unreadCount === 1 ? '' : 's'}`;

  function handleMarkAllRead() {
    if (unreadIds.length > 0) {
      markReadMutation.mutate({ notification_ids: unreadIds });
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={badgeAriaLabel}
          className={cn(
            'relative p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
          )}
        >
          <Bell size={18} aria-hidden="true" />
          {unreadCount > 0 && (
            <span
              aria-hidden="true"
              className={cn(
                'absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full',
                'bg-destructive text-destructive-foreground text-[9px] font-bold',
                'flex items-center justify-center leading-none tabular-nums',
                // Pulse suppressed under prefers-reduced-motion
                'motion-safe:animate-pulse'
              )}
            >
              {badgeText}
            </span>
          )}
        </button>
      </PopoverTrigger>

      <PopoverContent
        align="end"
        sideOffset={8}
        className="w-80 p-0 shadow-lg"
        aria-label="Notifications panel"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <h2 className="text-sm font-semibold text-foreground">Notifications</h2>
          <button
            type="button"
            onClick={() => setUnreadOnly((v) => !v)}
            className={cn(
              'text-[11px] px-2 py-0.5 rounded-full border transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              unreadOnly
                ? 'bg-primary text-primary-foreground border-primary'
                : 'text-muted-foreground border-border hover:border-primary/50 hover:text-foreground'
            )}
          >
            Unread only
          </button>
        </div>

        {/* Notification list */}
        <div className="max-h-[360px] overflow-y-auto divide-y divide-border/60">
          {isLoading ? (
            <div className="p-3 space-y-3">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="space-y-1.5">
                  <Skeleton className="h-3 w-16" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-3 w-3/4" />
                </div>
              ))}
            </div>
          ) : notifications.length === 0 ? (
            <div className="px-4 py-10 text-center">
              <Bell className="h-8 w-8 text-muted-foreground mx-auto mb-2" aria-hidden="true" />
              <p className="text-sm text-muted-foreground">
                {unreadOnly
                  ? "You're all caught up — nothing new to read."
                  : 'No notifications yet.'}
              </p>
            </div>
          ) : (
            notifications.map((n) => (
              <NotificationCard
                key={n.id}
                notification={n}
                compact
              />
            ))
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-4 py-2.5 border-t border-border bg-muted/30">
          <Button
            variant="ghost"
            size="sm"
            className="text-xs h-7 px-2 text-muted-foreground"
            onClick={handleMarkAllRead}
            disabled={unreadIds.length === 0 || markReadMutation.isPending}
          >
            Mark all as read
          </Button>
          <Link
            to="/notifications"
            className={cn(
              'text-xs text-primary hover:underline font-medium',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm'
            )}
            onClick={() => setOpen(false)}
          >
            See all
          </Link>
        </div>
      </PopoverContent>
    </Popover>
  );
}
